import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, ArrowLeft, ExternalLink } from "lucide-react";

export default function TeamBrandingEdit() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ logo_url: "", description: "", social: { website: "", instagram: "", twitter: "" } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/teams/${slug}`);
        setTeam(data);
        setForm({
          logo_url: data.logo_url || "",
          description: data.description || "",
          social: { website: data.social?.website || "", instagram: data.social?.instagram || "", twitter: data.social?.twitter || "" },
        });
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return <div className="p-10 text-zinc-400">Loading…</div>;
  if (!team) return <div className="p-10 text-zinc-400">Team not found.</div>;

  const canEdit = user && (user.role === "super_admin" || user.role === "league_admin" || (user.role === "owner" && user.team_id === team.id));
  if (!canEdit) {
    return (
      <div className="p-10 max-w-md mx-auto text-center" data-testid="team-edit-forbidden">
        <div className="font-anton text-3xl uppercase text-white">Not your team</div>
        <p className="text-zinc-400 mt-3">You can only edit the team you own.</p>
        <Button asChild className="mt-5 bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest"><Link to={`/team/${slug}`}>← Back to Team</Link></Button>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/teams/${team.id}/branding`, form);
      toast.success("Saved");
      nav(`/team/${team.slug}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="team-edit-page">
      <Link to={`/team/${team.slug}`} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center" data-testid="team-edit-back"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Team</Link>
      <div className="mt-4 mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Edit Team Page</div>
        <h1 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white">{team.name}</h1>
        <div className="text-xs text-zinc-400 mt-1">Public URL: <a href={`/team/${team.slug}`} className="text-[#00A86B] hover:underline" data-testid="team-edit-url">/team/{team.slug} <ExternalLink className="w-3 h-3 inline" /></a></div>
      </div>

      <Card className="p-6 bg-[#11241b] border-white/10 space-y-4" data-testid="team-edit-form">
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Logo URL</Label>
          <div className="flex items-center gap-3 mt-1">
            {form.logo_url && <img src={form.logo_url} alt="" className="h-12 w-12 object-contain bg-white rounded p-1" />}
            <Input data-testid="team-edit-logo" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." className="bg-[#060d09] border-white/10 text-white" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">About the team</Label>
          <Textarea data-testid="team-edit-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell fans about your team…" className="bg-[#060d09] border-white/10 text-white mt-1" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Website</Label>
            <Input data-testid="team-edit-website" value={form.social.website} onChange={(e) => setForm({ ...form, social: { ...form.social, website: e.target.value } })} placeholder="https://" className="bg-[#060d09] border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Instagram</Label>
            <Input data-testid="team-edit-instagram" value={form.social.instagram} onChange={(e) => setForm({ ...form, social: { ...form.social, instagram: e.target.value } })} placeholder="https://instagram.com/..." className="bg-[#060d09] border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Twitter / X</Label>
            <Input data-testid="team-edit-twitter" value={form.social.twitter} onChange={(e) => setForm({ ...form, social: { ...form.social, twitter: e.target.value } })} placeholder="https://twitter.com/..." className="bg-[#060d09] border-white/10 text-white mt-1" />
          </div>
        </div>
        <Button data-testid="team-edit-save" disabled={saving} onClick={save} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Card>
    </div>
  );
}
